import * as Sentry from "@sentry/react";
import React, { useState } from "react";

import { AdminContainer } from "../../../components/Admin";
import PageTitle from "../../../components/PageTitle";
import Spacer from "../../../components/Spacer";
import CompanySettingsSection from "./components/CompanySettingsSection";
import CustomBrandingSection from "./components/CustomBrandingSection";
import HeaderRow from "./components/HeaderRow";
import NewNavigationRolloutBanner from "./components/NewNavigationRolloutBanner";
import PerformYardFeaturesSection from "./components/PerformYardFeaturesSection";
import SeatStatCards from "./components/SeatStatCards";

import { useAdminSettingsHomeSeatUsage, useCompanySettings, useRbacPrimaryRolePermissions } from "../../../api/hooks";

import {
	adminSettingsHomeBrandingQuery,
	adminSettingsHomeFunctionsQuery,
	adminSettingsHomeSeatUsageQuery,
	adminSettingsHomeSettingsQuery,
	companySettingsQuery,
	currentUserQuery,
	rbacPermissionsQuery,
} from "../../../api/queries";
import { dataLoader } from "../../../api/queries/dataLoader";
import { queryClient } from "../../../api/queryClient";

import ErrorPage from "../../Error";
import { isFeatureNotEnabledError } from "./featureFlagError";

const readTargetCompanyId = (): string | undefined =>
	new URLSearchParams(window.location.search).get("companyId") ?? undefined;

const ensureQueryAllowFFOff = async <T,>(query: {
	queryKey: readonly unknown[];
	queryFn: () => Promise<T>;
}): Promise<void> => {
	try {
		await queryClient.ensureQueryData(query as Parameters<typeof queryClient.ensureQueryData>[0]);
	} catch (error) {
		// FF-off is the expected legacy path; the query cache already holds the
		// error so the page can yield to the Tornado-rendered legacy template.
		if (!isFeatureNotEnabledError(error)) throw error;
	}
};

export const adminSettingsHomeLoader = async () => {
	const companyId = readTargetCompanyId();
	await dataLoader([[currentUserQuery], [companySettingsQuery]]);
	const permissions = await queryClient.ensureQueryData(rbacPermissionsQuery("company_settings", companyId));
	const canViewHome = permissions.includes("company_settings:viewHome");
	const canViewBranding = permissions.includes("company_settings:viewBranding");
	const canViewFeatures = permissions.includes("company_settings:viewFeatures");
	const canViewSettings = permissions.includes("company_settings:viewSettings");

	if (!canViewHome) return null;

	await Promise.all([
		ensureQueryAllowFFOff(adminSettingsHomeSeatUsageQuery(companyId)),
		...(canViewBranding ? [ensureQueryAllowFFOff(adminSettingsHomeBrandingQuery(companyId))] : []),
		...(canViewFeatures ? [ensureQueryAllowFFOff(adminSettingsHomeFunctionsQuery(companyId))] : []),
		...(canViewSettings ? [ensureQueryAllowFFOff(adminSettingsHomeSettingsQuery(companyId))] : []),
	]);
	return null;
};

type OpenSection = "branding" | "features" | "settings" | null;

const AdminSettingsHomePage: React.FC = () => {
	const targetCompanyId = readTargetCompanyId();
	const { data: permissions, isLoading: permissionsLoading } = useRbacPrimaryRolePermissions(
		"company_settings",
		targetCompanyId
	);
	const { data: company } = useCompanySettings();
	const seatUsage = useAdminSettingsHomeSeatUsage(targetCompanyId);
	const [openSection, setOpenSection] = useState<OpenSection>(null);

	const toggle = (section: Exclude<OpenSection, null>) => () =>
		setOpenSection((prev) => (prev === section ? null : section));

	// FF-off: Tornado is serving the legacy template; yield rendering to it.
	if (isFeatureNotEnabledError(seatUsage.error)) return null;

	if (permissionsLoading) return null;

	const resolvedPermissions = permissions ?? [];

	if (!resolvedPermissions.includes("company_settings:viewHome")) {
		return <ErrorPage code={403} />;
	}

	const canViewBranding = resolvedPermissions.includes("company_settings:viewBranding");
	const canEditBranding = resolvedPermissions.includes("company_settings:editBranding");
	const canViewFeatures = resolvedPermissions.includes("company_settings:viewFeatures");
	const canEditFeatures = resolvedPermissions.includes("company_settings:editFeatures");
	const canViewSettings = resolvedPermissions.includes("company_settings:viewSettings");
	const canEditSettings = resolvedPermissions.includes("company_settings:editSettings");

	return (
		<Sentry.ErrorBoundary fallback={<ErrorPage code={500} />}>
			<PageTitle title="Company Settings" />
			<AdminContainer className="mx-auto w-full max-w-[1108px]">
				<div className="flex flex-col gap-8">
					<HeaderRow companyName={seatUsage.data?.companyName ?? company?.company?.name ?? "Company"} />
					<div className="flex flex-col gap-4">
						<SeatStatCards companyId={targetCompanyId} />
						<NewNavigationRolloutBanner
							canViewBranding={canViewBranding}
							companyId={targetCompanyId}
							onViewSetting={() => setOpenSection("branding")}
						/>
						{canViewBranding ? (
							<CustomBrandingSection
								isOpen={openSection === "branding"}
								onToggle={toggle("branding")}
								canEdit={canEditBranding}
								companyId={targetCompanyId}
							/>
						) : null}
						{canViewFeatures ? (
							<PerformYardFeaturesSection
								isOpen={openSection === "features"}
								onToggle={toggle("features")}
								canEdit={canEditFeatures}
								companyId={targetCompanyId}
							/>
						) : null}
						{canViewSettings ? (
							<CompanySettingsSection
								isOpen={openSection === "settings"}
								onToggle={toggle("settings")}
								canEdit={canEditSettings}
								companyId={targetCompanyId}
							/>
						) : null}
					</div>
				</div>
				<Spacer height={48} />
			</AdminContainer>
		</Sentry.ErrorBoundary>
	);
};

export default AdminSettingsHomePage;
